// Key changes marked with // NEW or // CHANGED

import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { Table, Row, Col, Input, Button, Tag, Space, Tooltip, Popconfirm, message } from 'antd';
import {
    SearchOutlined, ControlOutlined, PauseCircleOutlined,
    PlayCircleOutlined, PoweroffOutlined, ReloadOutlined
} from '@ant-design/icons';
import axios from '../../../config/axiosInstance';

const DSYNC_PRINCIPAL_HOST = 'dsync.lambdalogic.de';

// NEW: 6 minutes threshold in ms
const OFFLINE_THRESHOLD_MS = 6 * 60 * 1000;

// NEW: get last-seen timestamp from known fields
const getLastSeenTs = (r) => {
    // Try common fields, fallback to null
    const t = r?.lastSeenAt || r?.lastHeartbeatAt || r?.updatedAt || r?.network?.lastSeenAt;
    return t ? new Date(t).getTime() : null;
};

// NEW: compute if stale by last seen
const isStaleByLastSeen = (r) => {
    const ts = getLastSeenTs(r);
    if (!ts) return false; // no data → do not force offline
    return Date.now() - ts > OFFLINE_THRESHOLD_MS;
};

const getIsPaused = (r) => r?.status === 'paused' || !!r?.paused;

const isProtected = (r) => {
    const h = (r?.lastObservedHost || '').toLowerCase().replace(/:\d+$/, '');
    return h === DSYNC_PRINCIPAL_HOST;
};

// NEW: derive visual status with last-seen rule
const deriveStatus = (r) => {
    if (getIsPaused(r)) return 'paused';
    if (isStaleByLastSeen(r)) return 'offline';
    if (r?.online || r?.status === 'online') return 'online';
    if (r?.status === 'offline') return 'offline';
    return 'unknown';
};

const StatusPill = ({ status }) => {
    // CHANGED: now receives final status only
    if (status === 'paused') return <Tag color="gold">PAUSED</Tag>;
    if (status === 'online') return <Tag color="green">ONLINE</Tag>;
    if (status === 'offline') return <Tag color="red">OFFLINE</Tag>;
    return <Tag>UNKNOWN</Tag>;
};

const formatRelative = (ts) => {
    // very small relative formatter without extra deps
    if (!ts) return '—';
    const diff = Date.now() - ts;
    if (diff < 0) return 'now';
    const sec = Math.floor(diff / 1000);
    if (sec < 60) return `${sec}s ago`;
    const min = Math.floor(sec / 60);
    if (min < 60) return `${min}m ago`;
    const hrs = Math.floor(min / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    return `${days}d ago`;
};

const ServersTable = ({ onOpenServerModal, autoRefreshMs = 60000 }) => {
    const [servers, setServers] = useState([]);
    const [search, setSearch] = useState('');
    const [loading, setLoading] = useState(false);

    const fetchServers = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await axios.get('/control/agents');
            setServers(data?.agents || []);
        } catch {
            setServers([]);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { fetchServers(); }, [fetchServers]);

    useEffect(() => {
        if (!autoRefreshMs) return;
        const id = setInterval(fetchServers, autoRefreshMs);
        return () => clearInterval(id);
    }, [fetchServers, autoRefreshMs]);

    const act = async (id, action, body = {}) => {
        setServers(prev => prev.map(s => {
            if (s.instanceId !== id) return s;
            if (action === 'pause')  return { ...s, paused: true,  status: 'paused' };
            if (action === 'resume') return { ...s, paused: false, status: 'online' };
            return s;
        }));

        try {
            const { data } = await axios.post(`/control/agents/${id}/${action}`, body);
            message.success(`${action.toUpperCase()} sent`);

            if (data?.agent) {
                setServers(prev => prev.map(s =>
                    s.instanceId === id
                        ? { ...s, ...data.agent, online: s.online }
                        : s
                ));
            } else {
                await fetchServers();
            }
        } catch (e) {
            message.error(`Failed to ${action}`);
            await fetchServers();
        }
    };

    // CHANGED: apply search over derived status too
    const filtered = useMemo(() => {
        const v = (search || '').toLowerCase();
        if (!v) return servers;
        return servers.filter(s => {
            const derived = deriveStatus(s);
            return (
                (s.instanceId || '').toLowerCase().includes(v) ||
                (s.lastObservedHost || '').toLowerCase().includes(v) ||
                (s.system?.hostname || '').toLowerCase().includes(v) ||
                (s.backendVersion || '').toLowerCase().includes(v) ||
                (s.status || '').toLowerCase().includes(v) ||
                derived.includes(v) ||
                (s.network?.geo?.city || '').toLowerCase().includes(v) ||
                (s.network?.geo?.country || '').toLowerCase().includes(v)
            );
        });
    }, [search, servers]);

    const columns = [
        {
            title: 'Host',
            key: 'host',
            render: (_, r) => {
                const raw = r.system?.hostname || r.lastObservedHost || '';
                const host = raw.split('.')[0] || null;
                return host ? (
                    <Tooltip title={`Instance: ${r.instanceId}`}>
                        <Space size="small">
                            <Tag color="blue">{host}</Tag>
                            {isProtected(r) && <Tag color="gold">PRIMARY</Tag>}
                        </Space>
                    </Tooltip>
                ) : '—';
            },
            width: 240,
        },
        {
            title: 'Domain',
            dataIndex: 'lastObservedHost',
            key: 'domain',
            render: (v, r) => (
                <Space size="small">
                    {v ? <Tag>{v}</Tag> : '—'}
                    {isProtected(r) && <Tag color="geekblue">dsync</Tag>}
                </Space>
            ),
            width: 260
        },
        {
            title: 'Location',
            key: 'location',
            render: (_, r) => {
                const city = r.network?.geo?.city;
                const country = r.network?.geo?.country;
                const label = [city, country].filter(Boolean).join(', ');
                return label ? <Tag color="geekblue">{label}</Tag> : '—';
            },
            width: 180
        },
        {
            title: 'Backend',
            dataIndex: 'backendVersion',
            key: 'backendVersion',
            render: (v) => v ? <Tag color="volcano">{v}</Tag> : '—',
            width: 120
        },
        {
            title: 'Status',
            key: 'status',
            // CHANGED: show derived status (with last-seen rule)
            render: (_, r) => <StatusPill status={deriveStatus(r)} />,
            width: 110
        },
        // NEW: Optional Last seen column (can be removed if not needed)
        {
            title: 'Last seen',
            key: 'lastSeen',
            render: (_, r) => {
                const ts = getLastSeenTs(r);
                return (
                    <Tooltip title={ts ? new Date(ts).toLocaleString() : 'unknown'}>
                        <span>{formatRelative(ts)}</span>
                    </Tooltip>
                );
            },
            width: 140
        },
        {
            title: 'Actions',
            key: 'actions',
            render: (_, r) => {
                const isPaused = getIsPaused(r);
                const status = deriveStatus(r); // CHANGED
                const isOffline = status === 'offline'; // CHANGED
                const locked = isProtected(r);
                const disabled = isOffline || locked;

                return (
                    <Space>
                        <Tooltip title="Open control">
                            <Button icon={<ControlOutlined />} onClick={() => onOpenServerModal?.(r)} />
                        </Tooltip>

                        {isPaused ? (
                            <Tooltip title={locked ? 'Primary server cannot be resumed here' : 'Resume'}>
                                <Button
                                    type="primary"
                                    icon={<PlayCircleOutlined />}
                                    onClick={() => act(r.instanceId, 'resume')}
                                    disabled={disabled}
                                />
                            </Tooltip>
                        ) : (
                            <Tooltip title={locked ? 'Primary server cannot be paused' : 'Pause'}>
                                <Button
                                    style={{
                                        borderStyle: 'dashed',
                                        borderColor: '#fa8c16',
                                        color: '#fa8c16'
                                    }}
                                    icon={<PauseCircleOutlined />}
                                    onClick={() => act(r.instanceId, 'pause', { reason: 'manual' })}
                                    disabled={disabled}
                                />
                            </Tooltip>
                        )}

                        <Popconfirm
                            title={locked ? 'Primary server cannot be restarted here' : 'Restart this server?'}
                            okText="Yes"
                            cancelText="No"
                            onConfirm={() => act(r.instanceId, 'restart')}
                            disabled={locked}
                        >
                            <Tooltip title={locked ? 'Primary server cannot be restarted' : 'Restart'}>
                                <Button danger icon={<PoweroffOutlined />} disabled={locked} />
                            </Tooltip>
                        </Popconfirm>
                    </Space>
                );
            },
            width: 320 // slightly wider because of the new column
        }
    ];

    return (
        <div>
            <Row gutter={[16,16]} align="middle" style={{ marginBottom: 24 }}>
                <Col xs={20} sm={12} md={10} lg={8}>
                    <Input
                        placeholder="Search by instance, host, version, status"
                        prefix={<SearchOutlined />}
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                    />
                </Col>
                <Col xs={4} sm={24} md={8} lg={4}>
                    <Button icon={<ReloadOutlined />} onClick={fetchServers} loading={loading}>Refresh</Button>
                </Col>
            </Row>

            <Table
                columns={columns}
                dataSource={filtered}
                rowKey={(r) => r.instanceId}
                loading={loading}
                scroll={{ x: 'max-content' }}
            />
        </div>
    );
};

export default ServersTable;
